import React from "react"
import type { StyleProps } from "@mallardbay/lib-react-components"

import PaymentPlanForm from "~components/pages/book/payment-plan-form"
import PaymentFormNote from "~components/pages/book/payment-form-note"
import GroupPay from "~components/pages/book/group-pay"
import Divider from "~components/shared/divider"
import { type QuoteFieldsFragment } from "~graphql/generated/graphql"
import { useBookTrip } from "~components/pages/book/book-trip-provider"
import GuestOrUserPaymentMethodSelectForm from "~components/pages/account/payment-methods/guest-or-user-payment-method-select-form"
import Error from "~components/shared/error"
import COPY from "~config/copy-constants"
import { TextLarge } from "~components/shared/todo-lib-react-components/text"
import Box from "~components/shared/todo-lib-react-components/box"
import { useTrackPaymentMethodSelected } from "~utils/event-hooks"

interface Props {
    readonly quote: QuoteFieldsFragment
}

export default function PaymentForm({ quote }: Props) {
    const styles = useStyles()

    const paymentPlans = quote.payment_group
        ? quote.payment_group.host.payment_plans
        : quote.payment_plans

    const {
        paymentPlanType,
        setPaymentPlanType,
        paymentMethodState,
        updatePaymentMethodState,
        isLoadingQuote,
        bookingError,
    } = useBookTrip()

    const { paymentMethodId, isExternalPaymentMethod, isAchPaymentMethod } =
        paymentMethodState

    useTrackPaymentMethodSelected({
        quoteId: quote.id,
        paymentMethodId,
        isAchPaymentMethod,
        isExternalPaymentMethod,
    })

    const isPayWithCheckEnabled = Boolean(quote.should_allow_external_payment)

    return (
        <Box>
            <GroupPay quote={quote} />

            <TextLarge style={styles.title}>
                {COPY.HOW_DO_YOU_WANT_TO_PAY}
            </TextLarge>
            <PaymentPlanForm
                value={paymentPlanType}
                onChange={(newType) => setPaymentPlanType(newType)}
                paymentPlans={paymentPlans}
                isLoading={isLoadingQuote}
            />
            <Divider margin />

            <TextLarge style={styles.title}>
                {COPY.SELECT_PAYMENT_METHOD}
            </TextLarge>
            <GuestOrUserPaymentMethodSelectForm
                selectedId={paymentMethodId}
                onChange={updatePaymentMethodState}
                isPayWithCheckEnabled={isPayWithCheckEnabled}
                isExternalPaymentMethod={isExternalPaymentMethod}
            />

            {bookingError && <Error errorMsg={bookingError.message} />}
            <Divider margin />

            <PaymentFormNote />
        </Box>
    )
}

function useStyles() {
    const title: StyleProps = {
        marginBottom: 2,
    }

    return { title }
}
